import { ENV } from '@/config/env';
import { StockQuote, StockDataResponse, StockChartData } from '@/types/stock.types';
import { polygonChartDataService } from './polygonChartData.service';
import { DEFAULT_TIMEFRAME } from '@/constants/chartTimeframes';

/**
 * Polygon.io Market Data Service
 *
 * Provides real-time market data using Polygon.io's efficient batch APIs
 * - Batch snapshot: Get up to 250 stocks in ONE API call
 * - Smart caching: Reduce API calls by 80%
 * - Real-time quotes on paid plans, 15-min delayed on free tier
 */

// ==================== TYPES ====================

export interface PolygonQuote {
  ticker: string;
  name?: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  marketCap?: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
  timestamp: number;
}

export interface PolygonSnapshot {
  ticker: string;
  todaysChangePerc: number;
  todaysChange: number;
  day?: {
    o: number; // open
    h: number; // high
    l: number; // low
    c: number; // close
    v: number; // volume
    vw: number; // volume weighted
  };
  min?: {
    av: number; // accumulated volume
    c: number; // close
    h: number; // high
    l: number; // low
    o: number; // open
    v: number; // volume
  };
  prevDay?: {
    c: number; // close
    h: number; // high
    l: number; // low
    o: number; // open
    v: number; // volume
  };
  updated: number;
}

export interface PolygonBatchResponse {
  status: string;
  count: number;
  tickers: PolygonSnapshot[];
}

export interface PolygonIndexSnapshot {
  ticker: string;
  name?: string;
  value: number;
  session?: {
    change: number;
    change_percent: number;
    high: number;
    low: number;
    open: number;
    close: number;
    previous_close: number;
    volume?: number;
  };
  updated?: number;
}

export interface PolygonIndicesResponse {
  status: string;
  results: PolygonIndexSnapshot[];
}

export interface MarketMoverStock {
  ticker: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
}

// ==================== SERVICE ====================

class PolygonMarketDataService {
  private readonly baseUrl = 'https://api.polygon.io';
  private readonly apiKey: string;
  private memoryCache: Map<string, { data: any; expires: number }> = new Map();
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.apiKey = ENV.POLYGON_API_KEY || '';

    if (!this.apiKey) {
      console.warn('⚠️ [Polygon] API key not configured. Set POLYGON_API_KEY in .env');
    }
  }

  /**
   * Check if API is configured
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }

  /**
   * Get batch snapshot for multiple tickers (EFFICIENT - 1 API call!)
   *
   * @param tickers Array of stock symbols (up to 250)
   * @param useCache Whether to use memory cache
   * @returns Array of market quotes
   *
   * @example
   * const quotes = await polygonService.getBatchSnapshot(['AAPL', 'TSLA', 'NVDA']);
   */
  async getBatchSnapshot(
    tickers: string[],
    useCache: boolean = true
  ): Promise<{ data: PolygonQuote[] | null; error: string | null }> {
    try {
      if (!this.isConfigured()) {
        return {
          data: null,
          error: 'Polygon API key not configured',
        };
      }

      // Validate tickers
      if (!tickers || tickers.length === 0) {
        return { data: [], error: null };
      }

      if (tickers.length > 250) {
        console.warn(
          `⚠️ [Polygon] Batch limited to 250 tickers, truncating from ${tickers.length}`
        );
        tickers = tickers.slice(0, 250);
      }

      const cacheKey = `batch_${tickers.sort().join(',')}`;

      // Check memory cache
      if (useCache && this.memoryCache.has(cacheKey)) {
        const cached = this.memoryCache.get(cacheKey)!;
        if (Date.now() < cached.expires) {
          console.log(`✅ [Polygon] Cache hit for ${tickers.length} tickers`);
          return { data: cached.data, error: null };
        }
        this.memoryCache.delete(cacheKey);
      }

      console.log(`🔍 [Polygon] Fetching batch snapshot for ${tickers.length} tickers...`);

      const tickersParam = tickers.join(',');
      const url = `${this.baseUrl}/v2/snapshot/locale/us/markets/stocks/tickers?tickers=${tickersParam}&apiKey=${this.apiKey}`;

      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [Polygon] API error (${response.status}):`, errorText);

        if (response.status === 429) {
          return { data: null, error: 'Rate limit exceeded. Please try again in a moment.' };
        }
        if (response.status === 401 || response.status === 403) {
          return { data: null, error: 'Invalid API key or access denied.' };
        }

        return { data: null, error: `API error: ${response.status}` };
      }

      const json: PolygonBatchResponse = await response.json();

      if (json.status !== 'OK' || !json.tickers) {
        console.error('❌ [Polygon] Invalid response:', json);
        return { data: null, error: 'Invalid API response' };
      }

      // Transform to our Quote format
      const quotes: PolygonQuote[] = json.tickers.map(snapshot => this.transformSnapshot(snapshot));

      // Cache the result
      this.memoryCache.set(cacheKey, {
        data: quotes,
        expires: Date.now() + this.CACHE_TTL_MS,
      });

      console.log(`✅ [Polygon] Fetched ${quotes.length} stocks in 1 API call`);

      return { data: quotes, error: null };
    } catch (error) {
      console.error('❌ [Polygon] Batch snapshot error:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error fetching market data',
      };
    }
  }

  /**
   * Get top gainers of the day
   *
   * @param limit Number of gainers to return (default: 10)
   * @returns Array of top gaining stocks
   */
  async getTopGainers(
    limit: number = 10
  ): Promise<{ data: MarketMoverStock[] | null; error: string | null }> {
    try {
      if (!this.isConfigured()) {
        return { data: null, error: 'Polygon API key not configured' };
      }

      console.log(`📈 [Polygon] Fetching top ${limit} gainers...`);

      const url = `${this.baseUrl}/v2/snapshot/locale/us/markets/stocks/gainers?apiKey=${this.apiKey}`;
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`❌ [Polygon] Gainers API error (${response.status})`);
        return { data: null, error: `API error: ${response.status}` };
      }

      const json: PolygonBatchResponse = await response.json();

      if (json.status !== 'OK' || !json.tickers) {
        return { data: null, error: 'Invalid API response' };
      }

      const movers: MarketMoverStock[] = json.tickers.slice(0, limit).map(snapshot => ({
        ticker: snapshot.ticker,
        name: snapshot.ticker, // Polygon doesn't return name in gainers endpoint
        price: snapshot.day?.c || 0,
        change: snapshot.todaysChange || 0,
        changePercent: snapshot.todaysChangePerc || 0,
        volume: snapshot.day?.v || 0,
      }));

      console.log(`✅ [Polygon] Found ${movers.length} top gainers`);

      return { data: movers, error: null };
    } catch (error) {
      console.error('❌ [Polygon] Top gainers error:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error fetching gainers',
      };
    }
  }

  /**
   * Get top losers of the day
   *
   * @param limit Number of losers to return (default: 10)
   * @returns Array of top losing stocks
   */
  async getTopLosers(
    limit: number = 10
  ): Promise<{ data: MarketMoverStock[] | null; error: string | null }> {
    try {
      if (!this.isConfigured()) {
        return { data: null, error: 'Polygon API key not configured' };
      }

      console.log(`📉 [Polygon] Fetching top ${limit} losers...`);

      const url = `${this.baseUrl}/v2/snapshot/locale/us/markets/stocks/losers?apiKey=${this.apiKey}`;
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`❌ [Polygon] Losers API error (${response.status})`);
        return { data: null, error: `API error: ${response.status}` };
      }

      const json: PolygonBatchResponse = await response.json();

      if (json.status !== 'OK' || !json.tickers) {
        return { data: null, error: 'Invalid API response' };
      }

      const movers: MarketMoverStock[] = json.tickers.slice(0, limit).map(snapshot => ({
        ticker: snapshot.ticker,
        name: snapshot.ticker,
        price: snapshot.day?.c || 0,
        change: snapshot.todaysChange || 0,
        changePercent: snapshot.todaysChangePerc || 0,
        volume: snapshot.day?.v || 0,
      }));

      console.log(`✅ [Polygon] Found ${movers.length} top losers`);

      return { data: movers, error: null };
    } catch (error) {
      console.error('❌ [Polygon] Top losers error:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error fetching losers',
      };
    }
  }

  /**
   * Get stock quote for a single ticker
   *
   * @param symbol Stock symbol (e.g., "AAPL", "TSLA")
   * @param useCache Whether to use memory cache
   * @returns Stock quote matching StockQuote interface
   */
  async getStockQuote(
    symbol: string,
    useCache: boolean = true
  ): Promise<{ data: StockQuote | null; error: string | null }> {
    try {
      if (!this.isConfigured()) {
        return {
          data: null,
          error: 'Polygon API key not configured',
        };
      }

      const normalizedSymbol = symbol.trim().toUpperCase();
      const cacheKey = `quote_${normalizedSymbol}`;

      // Check memory cache
      if (useCache && this.memoryCache.has(cacheKey)) {
        const cached = this.memoryCache.get(cacheKey)!;
        if (Date.now() < cached.expires) {
          console.log(`✅ [Polygon] Quote cache hit for ${normalizedSymbol}`);
          return { data: cached.data, error: null };
        }
        this.memoryCache.delete(cacheKey);
      }

      console.log(`📊 [Polygon] Fetching quote for ${normalizedSymbol}...`);

      const url = `${this.baseUrl}/v2/snapshot/locale/us/markets/stocks/tickers/${normalizedSymbol}?apiKey=${this.apiKey}`;
      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [Polygon] Quote API error (${response.status}):`, errorText);

        if (response.status === 404) {
          return { data: null, error: 'Stock symbol not found' };
        }
        if (response.status === 429) {
          return { data: null, error: 'Rate limit exceeded. Please try again in a moment.' };
        }
        if (response.status === 401 || response.status === 403) {
          return { data: null, error: 'Invalid API key or access denied.' };
        }

        return { data: null, error: `API error: ${response.status}` };
      }

      const json: { status: string; ticker: PolygonSnapshot } = await response.json();

      if (json.status !== 'OK' || !json.ticker) {
        console.error('❌ [Polygon] Invalid quote response:', json);
        return { data: null, error: 'No quote data available for this symbol' };
      }

      const snapshot = json.ticker;
      const currentPrice = snapshot.day?.c || snapshot.min?.c || snapshot.prevDay?.c || 0;
      const previousClose = snapshot.prevDay?.c || 0;

      // Transform to StockQuote interface
      const quote: StockQuote = {
        symbol: normalizedSymbol,
        companyName: normalizedSymbol, // Polygon doesn't provide company name in snapshot
        latestPrice: currentPrice,
        change: snapshot.todaysChange || 0,
        changePercent: snapshot.todaysChangePerc || 0,
        high: snapshot.day?.h || 0,
        low: snapshot.day?.l || 0,
        open: snapshot.day?.o || 0,
        previousClose: previousClose,
        volume: snapshot.day?.v || 0,
        marketCap: 0, // Polygon snapshot doesn't include market cap
        peRatio: null, // Polygon snapshot doesn't include P/E ratio
      };

      // Cache the result
      this.memoryCache.set(cacheKey, {
        data: quote,
        expires: Date.now() + this.CACHE_TTL_MS,
      });

      console.log(`✅ [Polygon] Quote fetched for ${normalizedSymbol}: $${currentPrice}`);

      return { data: quote, error: null };
    } catch (error) {
      console.error('❌ [Polygon] Get stock quote error:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error fetching stock quote',
      };
    }
  }

  /**
   * Get complete stock data (quote + chart)
   * Compatible with existing StockDataResponse interface
   *
   * @param symbol Stock symbol
   * @param options Optional: bypassCache to force fresh data
   * @returns Complete stock data response
   */
  async getStockData(
    symbol: string,
    options: { bypassCache?: boolean } = {}
  ): Promise<StockDataResponse> {
    try {
      const normalizedSymbol = symbol.trim().toUpperCase();
      const cacheKey = `stock_data_${normalizedSymbol}`;

      // Check cache first (unless bypassed)
      if (!options.bypassCache && this.memoryCache.has(cacheKey)) {
        const cached = this.memoryCache.get(cacheKey)!;
        if (Date.now() < cached.expires) {
          console.log(
            `✅ [Polygon] Stock data cache hit for ${normalizedSymbol} (${Math.round((this.CACHE_TTL_MS - (cached.expires - Date.now())) / 1000)}s old)`
          );
          return cached.data;
        }
        this.memoryCache.delete(cacheKey);
      }

      console.log(`📡 [Polygon] Fetching complete stock data for ${normalizedSymbol}...`);

      // Fetch quote and chart in parallel
      const [quoteResult, chartResult] = await Promise.all([
        this.getStockQuote(normalizedSymbol, !options.bypassCache),
        polygonChartDataService.getChartData(normalizedSymbol, DEFAULT_TIMEFRAME),
      ]);

      // If quote fails, return error
      if (quoteResult.error) {
        return {
          quote: null,
          chartData: [],
          error: quoteResult.error,
        };
      }

      // Return quote with chart (even if chart fails)
      const stockData: StockDataResponse = {
        quote: quoteResult.data,
        chartData: chartResult.data || [],
        error: chartResult.error,
      };

      // Cache the result
      this.memoryCache.set(cacheKey, {
        data: stockData,
        expires: Date.now() + this.CACHE_TTL_MS,
      });

      console.log(`✅ [Polygon] Stock data complete for ${normalizedSymbol}`);

      return stockData;
    } catch (error) {
      console.error('❌ [Polygon] Get stock data error:', error);
      return {
        quote: null,
        chartData: [],
        error: 'An unexpected error occurred',
      };
    }
  }

  /**
   * Clear memory cache (useful for forcing refresh)
   */
  clearCache(): void {
    this.memoryCache.clear();
    console.log('🗑️ [Polygon] Memory cache cleared');
  }

  /**
   * Transform Polygon snapshot to our Quote format
   */
  private transformSnapshot(snapshot: PolygonSnapshot): PolygonQuote {
    const currentPrice = snapshot.day?.c || snapshot.min?.c || snapshot.prevDay?.c || 0;
    const previousClose = snapshot.prevDay?.c || 0;

    return {
      ticker: snapshot.ticker,
      price: currentPrice,
      change: snapshot.todaysChange || 0,
      changePercent: snapshot.todaysChangePerc || 0,
      volume: snapshot.day?.v || 0,
      high: snapshot.day?.h || 0,
      low: snapshot.day?.l || 0,
      open: snapshot.day?.o || 0,
      previousClose: previousClose,
      timestamp: snapshot.updated || Date.now(),
    };
  }

  /**
   * Check if market is currently open (simplified)
   * TODO: Use proper market calendar API
   */
  isMarketOpen(): boolean {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    const hour = now.getHours();

    // Weekend check
    if (day === 0 || day === 6) {
      return false;
    }

    // Market hours: 9:30 AM - 4:00 PM ET
    // This is simplified - should account for holidays
    return hour >= 9 && hour < 16;
  }

  /**
   * Get market status label
   */
  getMarketStatus(): string {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();

    if (day === 0 || day === 6) {
      return 'closed'; // Weekend
    }

    if (hour >= 4 && hour < 9) {
      return 'pre-market'; // 4:00 AM - 9:30 AM ET
    }

    if (hour >= 9 && hour < 16) {
      return 'open'; // 9:30 AM - 4:00 PM ET
    }

    if (hour >= 16 && hour < 20) {
      return 'after-hours'; // 4:00 PM - 8:00 PM ET
    }

    return 'closed';
  }

  /**
   * Get major market indices (S&P 500, NASDAQ, Dow Jones)
   * Uses Polygon's v3 indices snapshot endpoint
   *
   * @returns Array of index data
   */
  async getIndices(): Promise<{ data: PolygonQuote[] | null; error: string | null }> {
    try {
      if (!this.isConfigured()) {
        return {
          data: null,
          error: 'Polygon API key not configured',
        };
      }

      const cacheKey = 'indices_snapshot';

      // Check memory cache
      if (this.memoryCache.has(cacheKey)) {
        const cached = this.memoryCache.get(cacheKey)!;
        if (Date.now() < cached.expires) {
          console.log(`✅ [Polygon] Indices cache hit`);
          return { data: cached.data, error: null };
        }
        this.memoryCache.delete(cacheKey);
      }

      console.log(`🔍 [Polygon] Fetching indices from /v3/snapshot/indices...`);

      // Use v3 indices endpoint with ticker.any_of parameter
      const url = `${this.baseUrl}/v3/snapshot/indices?ticker.any_of=I:SPX,I:COMP,I:DJI&apiKey=${this.apiKey}`;

      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [Polygon] Indices API error (${response.status}):`, errorText);
        return { data: null, error: `API error: ${response.status}` };
      }

      const json: PolygonIndicesResponse = await response.json();

      console.log(`📊 [Polygon] Raw indices response:`, json.results?.length || 0, 'items');

      if (json.status !== 'OK' && json.status !== 'DELAYED') {
        console.error('❌ [Polygon] Invalid indices response status:', json.status);
        return { data: null, error: 'Invalid API response' };
      }

      if (!json.results || json.results.length === 0) {
        console.warn('⚠️ [Polygon] No indices data returned');
        return { data: [], error: null };
      }

      // Transform v3 indices response to our Quote format
      const quotes: PolygonQuote[] = json.results.map(index => {
        const session = index.session || {};
        const quote: PolygonQuote = {
          ticker: index.ticker,
          name: index.name || this.getIndexName(index.ticker),
          price: index.value || 0,
          change: session.change || 0,
          changePercent: session.change_percent || 0,
          volume: session.volume || 0,
          high: session.high || index.value || 0,
          low: session.low || index.value || 0,
          open: session.open || index.value || 0,
          previousClose: session.previous_close || index.value || 0,
          timestamp: index.updated || Date.now(),
        };
        return quote;
      });

      // Cache the result
      this.memoryCache.set(cacheKey, {
        data: quotes,
        expires: Date.now() + this.CACHE_TTL_MS,
      });

      console.log(`✅ [Polygon] Fetched ${quotes.length} indices successfully`);

      return { data: quotes, error: null };
    } catch (error) {
      console.error('❌ [Polygon] Indices fetch error:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error fetching indices',
      };
    }
  }

  /**
   * Get friendly name for index ticker
   */
  private getIndexName(ticker: string): string {
    const names: { [key: string]: string } = {
      'I:SPX': 'S&P 500',
      'I:COMP': 'NASDAQ Composite',
      'I:DJI': 'Dow Jones Industrial Average',
    };
    return names[ticker] || ticker;
  }
}

// Export singleton instance
export const polygonMarketDataService = new PolygonMarketDataService();
